import type { MRT_PaginationState } from 'material-react-table'
import { makeAutoObservable, runInAction } from 'mobx'
import { toast } from 'react-toastify'
import { visorPRORestClient } from '../../../../util/api'
import { observed } from '../../../../util/observed-decorator'
import { useEffect, useMemo } from 'react'
import { APIModelAliasReference } from '@visorpro/client'
import { APIUpsertModelAliasReferenceRequest } from '@visorpro/client/dist/RestClient/services'

export const getVirtualAliasId = (
    alias: Pick<APIModelAliasReference, 'input_manufacturer' | 'input_model'>,
) => {
    return `${alias.input_manufacturer}:${alias.input_model}`
}

export class WOModelMatchStore {
    public aliasesByKey: Record<string, APIModelAliasReference> = {}
    public aliasIds: string[] = []
    public isFetched = false
    public isFetching = false
    public isUpdating = false

    public currentPageIndex = 0
    public totalItems = 0

    constructor(
        private organizationId: string,
        private onChange?: () => void,
    ) {
        makeAutoObservable(this)
    }

    @observed('isFetching')
    public async load(pagination: MRT_PaginationState) {
        try {
            runInAction(() => {
                this.currentPageIndex = pagination.pageIndex
            })

            const response =
                await visorPRORestClient.organizationModelAlias.list(
                    this.organizationId,
                    {
                        offset: pagination.pageIndex * pagination.pageSize,
                        limit: pagination.pageSize,
                    },
                )

            const models: Record<string, APIModelAliasReference> = {}
            const modelIds: string[] = []

            response.items.forEach((model) => {
                const id = getVirtualAliasId(model)
                models[id] = model
                modelIds.push(id)
            })

            runInAction(() => {
                this.aliasesByKey = models
                this.aliasIds = modelIds
                this.isFetched = true
                this.totalItems = response.total
            })
        } catch (e) {
            toast.error(
                `Failed to fetch model aliases: ${(e as Error).message}`,
            )
        }
    }

    @observed('isUpdating')
    public async upsert(input: APIUpsertModelAliasReferenceRequest) {
        try {
            const updatedAlias =
                await visorPRORestClient.organizationModelAlias.upsert(
                    this.organizationId,
                    input,
                )

            this.onChange && this.onChange()

            runInAction(() => {
                const id = getVirtualAliasId(updatedAlias)
                this.aliasesByKey = {
                    ...this.aliasesByKey,
                    [id]: updatedAlias,
                }
            })
        } catch (e) {
            toast.error(`Failed to update model alias: ${(e as Error).message}`)
        }
    }
}

export const useModelAliases = (
    store: WOModelMatchStore,
    pagination: MRT_PaginationState,
) => {
    useEffect(() => {
        if (store.isFetching) {
            return
        }

        if (
            !store.isFetched ||
            pagination.pageIndex !== store.currentPageIndex
        ) {
            void store.load(pagination)
        }
    }, [store, pagination])

    return useMemo(() => {
        const models: APIModelAliasReference[] = Object.values(
            store.aliasesByKey,
        )
        models.sort((a, b) => a.input_model.localeCompare(b.input_model))
        return models
    }, [store.aliasesByKey])
}
